import React, { createContext, useContext, useMemo } from 'react';

import useLocalStorage from '@/hooks/useLocalStorage';

const STORAGE_KEY = 'sidebar-config';

type SidebarConfig = {
  navigationGroups: boolean;
};

const DEFAULT_CONFIG: SidebarConfig = {
  navigationGroups: false,
};

const parseSidebarConfig = (raw: unknown): SidebarConfig => {
  if (raw !== null && raw !== undefined && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    return {
      navigationGroups:
        typeof obj.navigationGroups === 'boolean'
          ? obj.navigationGroups
          : DEFAULT_CONFIG.navigationGroups,
    };
  }
  return DEFAULT_CONFIG;
};

type SidebarConfigContextValue = {
  navigationGroupsEnabled: boolean;
  setNavigationGroups: (value: boolean) => void;
};

const SidebarConfigContext = createContext<SidebarConfigContextValue>({
  navigationGroupsEnabled: DEFAULT_CONFIG.navigationGroups,
  setNavigationGroups: () => {},
});

export const SidebarConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [raw, setRaw] = useLocalStorage(STORAGE_KEY, DEFAULT_CONFIG);
  const config = parseSidebarConfig(raw);

  const value = useMemo<SidebarConfigContextValue>(() => ({
    navigationGroupsEnabled: config.navigationGroups,
    setNavigationGroups: (val: boolean) => setRaw({ ...config, navigationGroups: val }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [config.navigationGroups]);

  return (
    <SidebarConfigContext.Provider value={value}>
      {children}
    </SidebarConfigContext.Provider>
  );
};

export const useSidebarConfig = () => useContext(SidebarConfigContext);
